'use client';

import { Add } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { LeasingRight } from '../../../types/leases';
import { getDefaultEditModes, getEmptyCountAndRate } from '../../../utils';
import { isOwnerPortal, isTenantPortal } from '../../../utils/constants';
import { LeaseCountAndRate } from './leaseCountAndRate';

type LeaseCountAndRatesProps = {
	leasingRight: LeasingRight;
	refetchLeasingRight: (x?: any) => Promise<any>;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
	setIsCountAndRatesEditing: React.Dispatch<React.SetStateAction<boolean>>;
	isLeaseDetailEditInProgress: boolean;
};

export const LeaseCountAndRates: FC<LeaseCountAndRatesProps> = ({
	leasingRight,
	refetchLeasingRight,
	setShowDialog,
	setShowSnackbar,
	setIsCountAndRatesEditing,
	isLeaseDetailEditInProgress,
}) => {
	const [countAndRates, setCountAndRates] = useState(leasingRight.countAndRates);
	const [countAndRateEditModes, setCountAndRateEditModes] = useState(getDefaultEditModes(leasingRight.countAndRates));
	const [conversionRightEditModes, setConversionRightEditModes] = useState(
		getDefaultEditModes(leasingRight.countAndRates.flatMap((cr) => cr.conversionRights)),
	);
	const [countAndRateRateIds, setCountAndRateRateIds] = useState(
		leasingRight.countAndRates.reduce<{ [key: string]: string }>((acc, cr) => {
			acc[cr.id] = cr.rateId;
			return acc;
		}, {}),
	);
	const [countAndRateSublotIds, setCountAndRateSublotIds] = useState(
		leasingRight.countAndRates.reduce<{ [key: string]: number }>((acc, cr) => {
			acc[cr.id] = cr.sublotId;
			return acc;
		}, {}),
	);

	const pageEditInProgress = useMemo(
		() =>
			Object.values(countAndRateEditModes).some((editMode) => editMode) ||
			Object.values(conversionRightEditModes).some((editMode) => editMode),
		[conversionRightEditModes, countAndRateEditModes],
	);

	useEffect(() => {
		setIsCountAndRatesEditing?.(pageEditInProgress);
	}, [pageEditInProgress, setIsCountAndRatesEditing]);

	useEffect(() => {
		setCountAndRates(leasingRight.countAndRates);
		setCountAndRateEditModes(getDefaultEditModes(leasingRight.countAndRates));
		setConversionRightEditModes(
			getDefaultEditModes(leasingRight.countAndRates.flatMap((cr) => cr.conversionRights)),
		);
	}, [leasingRight]);

	const handleAdd = useCallback(() => {
		const newCountAndRate = getEmptyCountAndRate(leasingRight);

		setCountAndRates((prev) => [newCountAndRate, ...prev]);
		setCountAndRateEditModes((prev) => ({ ...prev, [newCountAndRate.id]: true }));
	}, [leasingRight]);

	const handleCancelNewMode = useCallback(() => {
		setCountAndRates((prev) => [
			...prev.reduce((acc, cr) => [...acc, ...(String(cr.id).includes('new') ? [] : [cr])], []),
		]);
	}, []);

	return (
		<Box>
			<Box display='flex' flexDirection='row' justifyContent='space-between' mt='1em' mb='1em'>
				<Box display='flex' flexDirection='row' gap='1em'>
					<Typography variant='h5' fontWeight='bold' alignContent='center'>
						Count & Rates
					</Typography>
					{!isLeaseDetailEditInProgress && !isTenantPortal && !isOwnerPortal && (
						<Button
							startIcon={<Add />}
							variant='contained'
							onClick={handleAdd}
							data-cy='add-count-and-rate-button'
						>
							Add
						</Button>
					)}
				</Box>
			</Box>
			<Box display='flex' flexDirection='column' gap='2em'>
				{countAndRates.map((countAndRate) => (
					<LeaseCountAndRate
						key={countAndRate.id}
						countAndRate={countAndRate}
						leasingRight={leasingRight}
						refetchLeasingRight={refetchLeasingRight}
						handleCancelNewMode={handleCancelNewMode}
						editMode={countAndRateEditModes[countAndRate.id]}
						setEditMode={(value: boolean) =>
							setCountAndRateEditModes((prev) => ({ ...prev, [countAndRate.id]: value }))
						}
						pageEditInProgress={isLeaseDetailEditInProgress}
						conversionRightEditModes={conversionRightEditModes}
						setConversionRightEditModes={setConversionRightEditModes}
						setChildrenGridApiRef={() => null}
						rateId={countAndRateRateIds[countAndRate.id]}
						setRateId={(rateId: string) =>
							setCountAndRateRateIds((prev) => ({
								...prev,
								[countAndRate.id]: rateId,
							}))
						}
						sublotId={countAndRateSublotIds[countAndRate.id]}
						setSublotId={(sublotId: number) =>
							setCountAndRateSublotIds((prev) => ({
								...prev,
								[countAndRate.id]: sublotId,
							}))
						}
						leaseNewMode={false}
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
					/>
				))}
			</Box>
		</Box>
	);
};
